/**
 * Служебные строки переписки сессии.
 *
 * Сервер кладёт в `text` и `kind` одно и то же слово — оно не для показа, а для выбора фразы:
 * переводить надо на стороне читателя, иначе строка, написанная при создании сессии, навсегда
 * осталась бы на языке того, кто её вызвал.
 */
export const SESSION_SYSTEM_KINDS = [
  'SESSION_STARTED',
  'SESSION_FINISHED',
  'SESSION_CANCELLED',
  'SESSION_MEMBER_JOINED',
  'SESSION_MEMBER_LEFT',
  /*
    Вход в голос – событие переписки, а не только яруса комнаты.

    Голос до сих пор жил отдельной жизнью: он виден в ярусе, но в ленте его не существовало, а
    смотрит вступивший именно в ленту. Строка «Юра зашёл в голос» отвечает там сразу на три
    вопроса: голос есть, в нём кто-то уже сидит, и это случилось только что.
  */
  'SESSION_VOICE_JOINED',
  /*
    Заявку удалили, а переписка осталась.

    До 20.09.2026 удаление заявки замораживало переписку навсегда и делало это для всей команды:
    автор убирал своё объявление, а трое собравшихся теряли место, где договаривались. Теперь
    разговор переживает заявку, и эта строка объясняет, почему сессия разом пропала со всех
    экранов: карточки, расписания и ссылки на заявку больше нет, а команда есть.
  */
  'SESSION_POST_DELETED',
] as const;

export type SessionSystemKind = (typeof SESSION_SYSTEM_KINDS)[number];

export const isSystemKind = (kind: string | null | undefined): kind is SessionSystemKind =>
  kind != null && (SESSION_SYSTEM_KINDS as readonly string[]).includes(kind);

/**
 * Разговор уходит за пределы площадки: в реплике появились чужая ссылка, мессенджер или контакт.
 *
 * Стоит особняком от строк сессии, и намеренно. Те сообщают о событии внутри переписки («вошёл в
 * голос») и выглядят компактным чипом по центру; эта говорит о границе сервиса и требует места
 * под две фразы, поэтому рисуется плашкой. Сервер ставит её один раз на собеседника.
 */
export const OFF_PLATFORM_NOTICE_KIND = 'OFF_PLATFORM_NOTICE';

export const isOffPlatformNoticeKind = (kind: string | null | undefined): boolean =>
  kind === OFF_PLATFORM_NOTICE_KIND;

/** Вид события, которое стоит в превью вместо реплики человека. */
export type ChatPreviewKind =
  | 'deleted'
  | 'sessionCreated'
  | 'sessionFinished'
  | 'sessionCancelled'
  | 'memberJoined'
  | 'memberLeft'
  | 'voiceJoined'
  | 'postDeleted'
  | 'offPlatform';

/**
 * Что стоит в превью вместо служебного слова сервера. `null` – обычная реплика человека.
 *
 * Помощник отдаёт ВИД события, а не готовый текст, и это принципиально. Прошлая версия брала
 * `t` параметром и звала внутри `t`A player joined``: `t` из `@lingui/react/macro` – макрос,
 * шаблон в обращение к каталогу превращает сборка и только в том модуле, где макрос
 * импортирован. Здесь превращения не происходило, в рантайм уезжал `i18n._(TemplateStringsArray)`,
 * а он на объекте вместо id возвращает ПУСТУЮ строку – превью списка чатов и комнаты молчали.
 *
 * Поэтому фразу подбирает каждый вызывающий у себя, макросом в своём компоненте, а сюда
 * никакой функции перевода не передаётся и передаваться не может.
 *
 * Имени в служебной строке нет намеренно: сервер кладёт только вид события, а додумывать
 * «кто» превью нечем – имя видно в самой переписке.
 */
export const resolveChatPreviewKind = (
  lastMessageText: string | null | undefined
): ChatPreviewKind | null => {
  if (!lastMessageText) {
    return null;
  }
  if (lastMessageText === 'DELETED') {
    return 'deleted';
  }
  /*
    Предупреждение об уходе за пределы площадки тоже становится последним событием переписки, а
    в список чатов уезжало сырым словом: строк сессии оно не касается, и проверка выше его не
    пускала. Примерно 740 переписок в месяц видели в превью технический код.
  */
  if (isOffPlatformNoticeKind(lastMessageText)) {
    return 'offPlatform';
  }
  if (!isSystemKind(lastMessageText)) {
    return null;
  }
  switch (lastMessageText) {
    case 'SESSION_MEMBER_JOINED':
      return 'memberJoined';
    case 'SESSION_MEMBER_LEFT':
      return 'memberLeft';
    case 'SESSION_VOICE_JOINED':
      return 'voiceJoined';
    case 'SESSION_FINISHED':
      return 'sessionFinished';
    case 'SESSION_CANCELLED':
      return 'sessionCancelled';
    case 'SESSION_POST_DELETED':
      return 'postDeleted';
    default:
      return 'sessionCreated';
  }
};
